import React from 'react';
import { Document, StyleSheet, Page, View, Text, Font, Image } from '@react-pdf/renderer';
import montserratLight from '../assets/fonts/Montserrat-Light.ttf'
import montserratBold from '../assets/fonts/Montserrat-Bold.ttf'
import Logo from '../assets/icons/R_logo.png';
import FooterLogo from '../assets/icons/R_footer.png';
import FlightArrow from '../assets/icons/flight.png';
import isEmpty from 'lodash.isempty';
import { PRINT_COLORS } from '../lib/printerHelper';
// Rendered outside the React tree by @react-pdf/renderer - use the standalone t().
import { t } from '../i18n';

Font.register({ family: 'Montserrat-Light', src: montserratLight });
Font.register({ family: 'Montserrat-Bold', src: montserratBold });

const { BLACK, BLUE, DARK_BLUE } = PRINT_COLORS;

const createStyles = (isMono) => StyleSheet.create({
  page: { padding: '30px 40px 0 40px', fontFamily: 'Montserrat-Light', fontSize: 9, color: isMono ? BLACK : DARK_BLUE },
  title: {
    fontFamily: 'Montserrat-Bold',
    fontSize: 14,
    textTransform: 'uppercase',
    borderBottomWidth: 1,
    borderColor: isMono ? BLACK : BLUE,
    width: '100%',
    color: isMono ? BLACK : BLUE,
    marginTop: 5,
    marginBottom: '13.72mm'
  },
  image: { width: 130, height: 'auto' },
  bold: { fontFamily: 'Montserrat-Bold', fontSize: 10 },
  important: { fontFamily: 'Montserrat-Bold', fontSize: 12 },
  transferContainer: { height: '17.27mm', marginTop: 7 },
  transferHeader: { fontSize: 13, marginTop: 10 },
  importantContainer: { height: '70.88mm' },
  detailsContainer: { height: '133mm' },
  warningContainer: { height: '8.5mm' },
  bottomLogoContainer: { height: '18.76mm' },
  bottomLogo: { marginLeft: 'auto', marginRight: -12, width: 30, height: 'auto' },
  header: { height: '20mm', flexDirection: 'row', justifyContent: 'space-between' },
  headerBackgroundArrow: { width: 30, height: 'auto', position: 'absolute', bottom: -20, left: '30%', zIndex: -1 },
  headerBackgroundText: { color: 'white', fontFamily: 'Montserrat-Bold', fontSize: 20, width: '100%', textAlign: 'center' },
  headerImage: { width: 'auto', height: 50, padding: '6px 0' },
  headerBackground: { backgroundColor: isMono ? '#fff' : '#00862f', padding: '5px 10px', borderRadius: 10, width: 200, height: 35 },
  topComment: { marginTop: 10, fontSize: 15 },
  bottomComment: { marginTop: 6 }
});

const createTableStyles = (isMono) => StyleSheet.create({
  mainContainer: { width: '100%', borderWidth: 1, borderColor: isMono ? BLACK : BLUE, borderRadius: 10, marginBottom: 10, fontFamily: 'Montserrat-Bold' },
  row: { width: '100%', flexDirection: 'row', height: '18mm' },
  responsiveRow: { width: '100%', flexDirection: 'row' },
  cell: { padding: '15px 5px 5px 5px', position: 'relative', fontSize: 10 },
  flightTo: { width: '20%', borderRightWidth: 1, borderColor: isMono ? BLACK : BLUE },
  flightNumber: { width: '11%' },
  dateTime: { width: '17%', borderColor: isMono ? BLACK : BLUE,  borderLeftWidth: 1, fontSize: 15 },
  meetingTime: { width: '19%', borderColor: isMono ? BLACK : BLUE,  borderLeftWidth: 1, fontSize: 15 },
  baggageAllowance: { width: '17%', borderColor: isMono ? BLACK : BLUE,  borderLeftWidth: 1 },
  meetingDetails: { width: '100%', borderColor: isMono ? BLACK : BLUE, borderTopWidth: 1 },
  dateInfo: { fontSize: 8, fontFamily: 'Montserrat-Bold', textAlign: 'center' },
  info: { fontSize: 8, position: 'absolute', top: 3, left: 5, fontFamily: 'Montserrat-Light' },
  centerText: { textAlign: 'center' }
});

export default function MyDocument(props) {
  const { data, isScaffold, topComment, header, theme } = props;
  const isMonoColor = theme !== 'color';
  const tableStyles = createTableStyles(isMonoColor);
  const styles = createStyles(isMonoColor);

  const generateTable = tableData => (
    <View style={tableStyles.mainContainer}>
      <View style={tableStyles.row}>
        <View style={[tableStyles.cell, tableStyles.flightTo]}>
          <Text style={tableStyles.info}>{t('pdf.flight.table.departureTo')}</Text>
          <Text>{tableData.wylotDo}</Text>
        </View>
        <View style={[tableStyles.cell, tableStyles.flightNumber]}>
          <Text style={tableStyles.info}>{t('pdf.flight.table.flightNumber')}</Text>
          <Text style={tableStyles.centerText}>{tableData.numerLotu}</Text>
        </View>
        <View style={[tableStyles.cell, tableStyles.meetingTime]}>
          <Text style={tableStyles.info}>{t('pdf.flight.table.meetingLocalTime')}</Text>
          <Text style={tableStyles.centerText}>{tableData.godzinaZbiorki}</Text>
          <Text style={tableStyles.dateInfo}>{tableData.dataZbiorki}</Text>
        </View>
        <View style={[tableStyles.cell, tableStyles.dateTime]}>
          <Text style={tableStyles.info}>{t('pdf.flight.table.departureLocalTime')}</Text>
          <Text style={tableStyles.centerText}>{tableData.godzinaWylotu}</Text>
          <Text style={tableStyles.dateInfo}>{tableData.dataWylotu}</Text>
        </View>
        <View style={[tableStyles.cell, tableStyles.dateTime]}>
          <Text style={tableStyles.info}>{t('pdf.flight.table.arrivalPolishTime')}</Text>
          <Text style={tableStyles.centerText}>{tableData.godzinaPrzylotu}</Text>
          <Text style={tableStyles.dateInfo}>{tableData.dataPrzylotu}</Text>
        </View>
        <View style={[tableStyles.cell, tableStyles.baggageAllowance]}>
          <Text style={tableStyles.info}>{t('pdf.flight.table.baggageAllowance')}</Text>
          <Text>{tableData.limitBagazu}</Text>
        </View>
      </View>
      {!isEmpty(tableData.uwagiZbiorki) && (
        <View style={tableStyles.responsiveRow}>
          <View style={[tableStyles.cell, tableStyles.meetingDetails]}>
            <Text style={tableStyles.info}>{t('pdf.flight.table.notes')}</Text>
            <Text>{tableData.uwagiZbiorki}</Text>
          </View>
        </View>
      )}
    </View>
  );
  
  return (
    <Document>
      {data.map((pageData, index) => (
        <Page key={`page-${index}`} size="A4" style={styles.page}>
          <View style={{ ...styles.header, opacity: isScaffold ? 0 : 1 }}>
            <Image src={Logo} style={styles.headerImage} />
            <View style={styles.headerBackground}>
              <Text style={styles.headerBackgroundText}>{t('pdf.flight.headerBadge')}</Text>
              <Image src={FlightArrow} style={styles.headerBackgroundArrow} />
            </View>
          </View>
          <View style={{ opacity: isScaffold ? 0 : 1 }}>
            <Text style={styles.title}>{t('pdf.flight.title')}</Text>
          </View>
          <View style={styles.transferContainer}>
            <Text style={{ ...styles.bold, opacity: isScaffold ? 0 : 1 }}>{t('pdf.flight.transferInfo')}</Text>
            <Text style={[styles.bold, styles.transferHeader]}>{header}</Text>
          </View>
          <View style={styles.importantContainer}>
            <Text style={{ ...styles.important, opacity: isScaffold ? 0 : 1 }}>{t('pdf.flight.importantInfo')}</Text>
            <Text style={styles.topComment}>{topComment}</Text>
          </View>
          <View style={styles.detailsContainer}>
            <Text style={{ ...styles.title, opacity: isScaffold ? 0 : 1 }}>{t('pdf.flight.detailedInfo')}</Text>
            {pageData.tables.map(flight => generateTable(flight))}
            <Text style={styles.bottomComment}>{pageData.comment}</Text>
          </View>
          <View style={{ ...styles.warningContainer, opacity: isScaffold ? 0 : 1 }}>
            <Text>{t('pdf.flight.checkTransferTime')}</Text>
          </View>
          <View style={{ ...styles.bottomLogoContainer, opacity: isScaffold ? 0 : 1 }}>
            <Image src={FooterLogo} style={styles.bottomLogo} />
          </View>
        </Page>
      ))}
    </Document>
  );
}
